import { useEffect, lazy, memo, Suspense } from 'react';
import { CenteredCircularProgress } from '../common/CenteredCircularProgress';
import { useDocumentation } from './useDocumentation';
import { useInitializeObjectsStructure } from './hooks/useInitializeObjectsStructure';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearDocumentationDialog,
  selectSingleObjectActiveTab,
  selectSingleObject,
  selectTransientObjects,
} from '../../reducers/documentation/salesforceDocumentationReducer';
import { useInitializeUserInputs } from './salesforce-documentation-card/hooks/useInitializeUserInputs';
import { usePrepareActiveObjectStructure } from './selected-object/usePrepareActiveObjectStructure';
import { useExpandedMode } from '../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { useSetQueryParamsOnMount } from '../../navigation-utils/useSetQueryParamsOnMount';
import { DeepLinkSearchParams } from '@server/deep-link-search-params';
import useQueryParams from '../../navigation-utils/useQueryParams';
import { useRunOnceWhenTruthy } from '../common/useRunOnceWhenTruthy';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { useDocumentationContext } from './context/DocumentationContext';
import { selectSaltoObjectTypes } from '../../reducers/global/parserReducer';

const ObjectListWrapper = lazy(() => import('./object-list/ObjectListWrapper'));
const SelectedObjectContent = lazy(() => import('./selected-object/SelectedObjectContent'));

interface ObjectsViewProps {
  isOpen: boolean;
  crmOrgId: string;
  onCloseDialog?: () => void;
  hideAddObjectButton?: boolean;
}

const useHandleDeepLinkParams = ({
  crmOrgId,
  isLoading,
}: {
  crmOrgId: string;
  isLoading: boolean;
}) => {
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));
  const objectTypeName = useSelector(selectSingleObject);
  const queryParams = useQueryParams();
  const { dispatchSingleObjectName } = useDocumentation();

  const bypassEntirely = !Boolean(objectTypeName?.objectType);
  useSetQueryParamsOnMount(
    {
      [DeepLinkSearchParams.objectName]: objectTypeName?.objectType,
    },
    bypassEntirely,
  );

  useRunOnceWhenTruthy(
    () => {
      const objectName = queryParams.get(DeepLinkSearchParams.objectName);
      const activeTabParam = queryParams.get(DeepLinkSearchParams.activeTab);

      const finalObjectName = objectName;
      const objectTypeName = saltoObjectTypes?.find((type) => type.objectType === finalObjectName);

      if (finalObjectName && objectTypeName) {
        const dispatchObj: {
          singleObjectName?: ObjectTypeName;
          tab?: DocumentationTabTypes;
        } = {
          singleObjectName: {
            objectType: objectTypeName.objectType,
            label: objectTypeName.label,
          },
        };
        if (activeTabParam) {
          dispatchObj.tab = activeTabParam as DocumentationTabTypes;
        }
        dispatchSingleObjectName(dispatchObj);
      }
    },
    Boolean(saltoObjectTypes && !isLoading),
  );
};

const ObjectsView = memo(
  ({
    crmOrgId,
    isOpen,
    onCloseDialog,
    hideAddObjectButton,
  }: ObjectsViewProps & { hideAddObjectButton?: boolean }) => {
    const dispatch = useDispatch();
    const objectsBeingParsed = useSelector(selectTransientObjects(crmOrgId));
    const { isExpandedMode } = useExpandedMode();
    const { canvasFunnelMap } = useDocumentationContext();
    const objectTypeName = useSelector(selectSingleObject);
    const isObjectBeingParsed = objectsBeingParsed?.[objectTypeName?.objectType ?? ''];
    const activeTab = useSelector(selectSingleObjectActiveTab);

    useHandleDeepLinkParams({
      crmOrgId,
      isLoading: isObjectBeingParsed,
    });

    useInitializeObjectsStructure({
      crmOrgId,
      funnelsData: canvasFunnelMap?.funnelsData,
      recordTypesData: canvasFunnelMap?.recordTypesData,
    });
    useInitializeUserInputs(crmOrgId);

    const { setActiveObjectElements, openOnFieldsTable } = useDocumentation();

    const list = usePrepareActiveObjectStructure(crmOrgId);

    useEffect(() => {
      if (objectTypeName?.objectType) {
        setActiveObjectElements(list);
      }
    }, [list, objectTypeName?.objectType, setActiveObjectElements]);

    useEffect(() => {
      if (!!isExpandedMode && activeTab === DocumentationTabTypes.CARDS_LIST) {
        openOnFieldsTable();
      }
    }, [isExpandedMode, activeTab, openOnFieldsTable]);

    useEffect(() => {
      return () => {
        dispatch(clearDocumentationDialog());
      };
    }, [dispatch]);

    const singleObjectName = useSelector(selectSingleObject);
    const displaySingleObject = !!singleObjectName;
    const displayObjectList = !singleObjectName;

    if (!isOpen) {
      return <></>;
    }

    return (
      <>
        {isObjectBeingParsed && displayObjectList && <CenteredCircularProgress />}

        {displayObjectList && (
          <Suspense fallback={<CenteredCircularProgress />}>
            <ObjectListWrapper
              crmOrgId={crmOrgId}
              onCloseDialog={onCloseDialog}
              hideAddObjectButton={hideAddObjectButton}
              withExpandedModeButton
            />
          </Suspense>
        )}

        <Stack
          direction="row"
          flexWrap="nowrap"
          height={displayObjectList ? 'auto' : '100%'}
          width="100%"
        >
          {displaySingleObject && (
            <Stack sx={{ width: '100%', height: '100%', minWidth: 0 }}>
              <Suspense fallback={<CenteredCircularProgress />}>
                <Box sx={{ flex: 1, minWidth: 0 }} data-testid="selected-object-content">
                  <SelectedObjectContent
                    crmOrgId={crmOrgId}
                    objectTypeName={singleObjectName}
                    onCloseDialog={onCloseDialog}
                    hideAddObjectButton={hideAddObjectButton}
                  />
                </Box>
              </Suspense>
            </Stack>
          )}
        </Stack>
      </>
    );
  },
);

export default ObjectsView;
